/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';

import PropTypes from 'prop-types';
// COMPONENTS
import Loader from '../../components/Loader/Loader';
import Error from '../../components/Error/Error';

// SUB PAGE COMPONENTS
import SingleProduct from './SingleProduct/SingleProduct';
import CustomizeProduct from './CustomizeProduct/CustomizeProduct';
import BoxProduct from './BoxProduct/BoxProduct';

// HELPERS
import { checkPath, routeCountryPath } from '../../shared/utilities/common';

import styles from './Product.module.css';
import { PRODUCT_QUERY, PRODUCT_QUERY_BY_SLUG } from '../../graphql/product';
import * as tagmanagerEvents from '../../shared/utilities/tagmanagerEvents';
import amplitudeAnalytics from '../../shared/utilities/amplitude';
import * as siteSettingRepo from '../../shared/repos/graphql/siteSetting';
import * as orderRepo from "../../shared/repos/graphql/order";
import { OrderEvent } from '../../shared/constants/OrderEvent';
import { isSampleStore } from '../../shared/constants/store';

import * as loyaltyAction from '../../redux/actions/loyalty';
import { PUNCHH_TYPE } from '../../shared/constants/loyaltyType';
import { handleCreateCart } from "../../shared/utilities/cart";
import * as userActions from "../../redux/actions/user";
import { isBrandRefreshEnabled } from '../../shared/utilities/brandRefresh';
import {
  hasCategorySlug,
  isBoxProduct
} from '../../shared/utilities/sleeves';

/** Category slugs that identify pack/box PDPs even when hasAssortments is false. */
const BOX_CATEGORY_SLUGS = [
  "boxes",
  "box",
  "classic_box_products",
  "packs",
  "pack"
];

/**
 * Box / pack PDP when API marks assortments, gift-sleeve box, or box category.
 * @param {{ hasAssortments?: unknown, options?: unknown[] | null, categories?: unknown[] }} product
 * @returns {boolean}
 */
export function shouldUseBoxProductPage(product) {
  if (!product) {
    return false;
  }
  if (product.hasAssortments) {
    return true;
  }
  if (isBoxProduct(product)) {
    return true;
  }
  return hasCategorySlug(product, BOX_CATEGORY_SLUGS);
}

/**
 * Chooses which PDP shell to mount from API fields.
 * @param {{ hasAssortments?: unknown, options?: unknown[] | null, categories?: unknown[] }} product
 * @returns {{ isBoxPath: boolean, isCustomizePath: boolean, isSinglePath: boolean, redirectProduct: string }}
 */
export function getProductPageRouteFlags(product) {
  const isBox = shouldUseBoxProductPage(product);
  const isCustom = product.options && product.options.length > 0;
  const isBoxPath = isBox;
  const isCustomizePath = isCustom && !isBox;
  let isSinglePath = false;
  let redirectProduct;
  if (isBoxPath) {
    redirectProduct = 'box';
  } else if (isCustomizePath) {
    redirectProduct = 'customize';
  } else {
    redirectProduct = 'single';
    isSinglePath = true;
  }
  return { isBoxPath, isCustomizePath, isSinglePath, redirectProduct };
}

class Product extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    /** Dedupe Amplitude View PDP per product id (parent re-renders while query data is stable). */
    this.lastAmplitudeViewPdpProductId = undefined;
  }

  componentDidMount() {
    const { order, userToken, setRedeemableItem, match, selectedStore, userAddress, userOrderMethod, userCartId, setUserCartId, setUserAddress } = this.props;
    if (userToken) {
      const orderRedeemable = order?.orderRedeemables?.find(p => {
        return p.order_product_id === match?.params?.poid
      });

      if (orderRedeemable) {
        setRedeemableItem({
          redeemableId: orderRedeemable?.external_id, // eslint-disable-line
          redeemableType: orderRedeemable?.redemption_group === "redeemable"  //eslint-disable-line
          ? PUNCHH_TYPE.REDEEMABLE
          : PUNCHH_TYPE.DEALS
        })
      }

      if (userAddress && (!userCartId || userCartId.length === 0)) {
        handleCreateCart(selectedStore, userAddress, userOrderMethod, setUserCartId, setUserAddress).then();
      }
    }
  }

  componentWillUnmount() {
    const { setRedeemableItem } = this.props;
    setRedeemableItem({
      redeemableType: null,
      redeemableId: null
    })
  }

  /**
   * Trigger Custom Cake Topper Conditionally
   *
   * @param productID
   * @param redirectProduct
   * @returns {Promise<boolean>}
   */
  triggerCustomCakeTopper = async (productID, redirectProduct) => {
    const {history, cakeTopperProductID, location} = this.props;
    const { displayProduct } = location.state || {displayProduct: false};
    const { isCartUpdate } = location.state || {isCartUpdate: false};

    const response = await siteSettingRepo.getSiteSettings(['topperoo']);
    const siteSettings = response.data.siteSettings[0];

    if ((siteSettings!=null && siteSettings.value === '1') && (productID === cakeTopperProductID) && (!displayProduct && !isCartUpdate)) {
      return history.push(routeCountryPath(`/products/custom-cake-topper/${cakeTopperProductID}`), {redirectProduct});
    }
    return true;
  };

  productContainer = () => {
    const isReviewPath = checkPath(this, '/checkout/view-order/');

    const { match, redeemables, selectedStore, order, userCartId, isShowOosProducts } = this.props;
    const { params } = match;
    const { id, slug, defaultPreset } = params;

    if(!id && !slug) {
      return (
        <Error errorType="404" />
      )
    }

    let productId = id;
    if (slug) {
      const response = useQuery(PRODUCT_QUERY_BY_SLUG, {
        variables: {
          slug,
        },
      });

      productId = response?.data?.productBySlug?.id;
    }
    const { data, loading, error } = useQuery(PRODUCT_QUERY, {
      variables: {
        id: productId,
        isShowOosProducts,
      },
      skip: !productId,
    });

    // NETWORK ERROR UI
    if (error) {
      return(
        <Error errorType="400" />
      )
    }

    // LOADING UI
    if (loading || !data || !data.product ) {
      return (
        <div className={styles.loadingPageContainer}>
          <div className={styles.loaderWrapper}>
            <Loader />
          </div>
        </div>
      );
    }
    const { product } = data;

    tagmanagerEvents.viewItem(product);
    if (this.lastAmplitudeViewPdpProductId !== product.id) {
      this.lastAmplitudeViewPdpProductId = product.id;
      amplitudeAnalytics.trackViewPdp({ product });
    }

    const { isBoxPath, isCustomizePath, isSinglePath, redirectProduct } = getProductPageRouteFlags(product);

    // Load Topperoo Widget conditionally when
    // Enabled from site settings backend

    const isStoreSelected = product.id && selectedStore?.id && !isSampleStore(selectedStore.id);
    const orderCode = order?.code || userCartId;

    if (isStoreSelected && orderCode) {
      const selectedStoreId = selectedStore.id.toString();
      orderRepo.orderEvent(OrderEvent.PRODUCT_VIEWED, orderCode, {productId: product.id, storeId: selectedStoreId})
    }

    this.triggerCustomCakeTopper(product.id, redirectProduct);

    // Brand-refresh shell: Single (#18953), Customize (#18955), Box (#18954)
    const isBrandRefreshPdp =
      isBrandRefreshEnabled() &&
      (isBoxPath || isCustomizePath || isSinglePath);
    const pageWrapperClass = isBrandRefreshPdp
      ? `${styles.productPageWrapper || ""} ${styles.brandRefreshProductPage}`
      : `${styles.productPageWrapper ||
          ""} w-full dark:bg-gradient-to-b dark:from-darkBlack dark:to-dark bg-white border-0`;
    const pageContainerClass = isBrandRefreshPdp
      ? `${styles.productPageContainer || ""} ${
          styles.brandRefreshProductContainer
        }`
      : `${styles.productPageContainer ||
          ""} lg:max-w-screen-desktop w-full lg:py-8 m-auto`;

    return (
      <div className={pageWrapperClass}>
        {/* <div className={styles.productBackground} /> */}
        {/* <div className={styles.solidBackground} /> */}
        {/* <div className={styles.tearSheetBackground} /> */}
        <Helmet>
          <title>{product.metaTitle || product.title}</title>
          <meta name="description" content={product.metaDescription || product.description} />
        </Helmet>
        <div className={pageContainerClass}>
          {isSinglePath ? (
            <SingleProduct
              updateProduct={isReviewPath}
              loyalty={redeemables?.redeemableType}
              productId={product.id}
            />
          ) : null}
          {isCustomizePath ? (
            <CustomizeProduct
              updateProduct={isReviewPath}
              loyalty={redeemables?.redeemableType}
              productId={product.id}
              selectedPreset={defaultPreset}
            />
          ) : null}
          {isBoxPath ? (
            <BoxProduct
              updateProduct={isReviewPath}
              loyalty={redeemables?.redeemableType}
              productId={product.id}
              selectedPreset={defaultPreset}
            />
          ) : null}
        </div>
      </div>
    );
  };

  render() {
    return <this.productContainer />;
  }
}

Product.propTypes = {
  cakeTopperProductID: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  isShowOosProducts: PropTypes.bool,
};

Product.defaultProps = {
  cakeTopperProductID: window.environment.REACT_APP_CUSTOM_CAKE_TOPPER_PRODUCT_ID,
  isShowOosProducts: false,
};

export const mapStateToProps = (state) => {
  const {
    userCartId,
    userCart,
    userToken,
    selectedStore,
    userAddress,
    userOrderMethod,
  } = state.user;
  const { order } = state;
  const { isShowOosProducts } = state.product;
  const {redeemables} = state.loyalty;
  return { userCartId, userCart, redeemables, order, userToken, selectedStore, isShowOosProducts, userAddress, userOrderMethod };
};

export const mapDispatchToProps = dispatch => ({
  setRedeemableItem: value => dispatch(loyaltyAction.setRedeemableItem(value)),
  setUserCartId: value => dispatch(userActions.setUserCartId(value)),
  setUserAddress: value => dispatch(userActions.setUserAddress(value)),
});


export default connect(mapStateToProps, mapDispatchToProps)(Product);

